/**
 * displayPrefs — preferensi tampilan per-user (tema + perilaku sidebar).
 * 2026-06-11 (DISPLAY-PREFS).
 *
 * Sumber kebenaran: backend `/user-prefs/display/appearance` (sinkron lintas
 * perangkat). localStorage `ens_display_prefs` dipakai sebagai cache supaya
 * tema terpasang instan sebelum response server tiba.
 */
import api from "@/lib/api";

export const DISPLAY_DEFAULTS = {
  theme: { mode: "light", darkStart: "19:00", darkEnd: "06:00" },
  // autoHide + hideDelaySec: 2026-06-19 (user request) — sidebar bisa diatur
  // apakah otomatis menyempit saat kursor keluar, dan setelah berapa detik.
  sidebar: { mode: "auto", openStart: "08:00", openEnd: "18:00", autoHide: true, hideDelaySec: 0.3 },
};

const LS_KEY = "ens_display_prefs";

export function mergeDisplayPrefs(raw) {
  return {
    theme: { ...DISPLAY_DEFAULTS.theme, ...(raw?.theme || {}) },
    sidebar: { ...DISPLAY_DEFAULTS.sidebar, ...(raw?.sidebar || {}) },
  };
}

export function readDisplayPrefsCache() {
  try { return mergeDisplayPrefs(JSON.parse(localStorage.getItem(LS_KEY) || "{}")); }
  catch { return mergeDisplayPrefs({}); }
}

export async function loadDisplayPrefs() {
  const cached = readDisplayPrefsCache();
  try {
    const { data } = await api.get("/user-prefs/display/appearance");
    const merged = mergeDisplayPrefs({ ...cached, ...data });
    localStorage.setItem(LS_KEY, JSON.stringify(merged));
    return merged;
  } catch { return cached; }
}

export async function saveDisplayPrefs(prefs) {
  const merged = mergeDisplayPrefs(prefs);
  localStorage.setItem(LS_KEY, JSON.stringify(merged));
  try { await api.put("/user-prefs/display/appearance", merged); } catch { /* offline — cache tetap berlaku */ }
  return merged;
}

const hhmmToMin = (s) => {
  const [h, m] = String(s || "00:00").split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
};

/** Apakah `date` berada di dalam jendela waktu [start, end) — mendukung jendela
 *  yang melewati tengah malam (mis. 19:00 → 06:00). */
export function isTimeInWindow(date, start, end) {
  const cur = date.getHours() * 60 + date.getMinutes();
  const s = hhmmToMin(start);
  const e = hhmmToMin(end);
  if (s === e) return true;
  return s < e ? (cur >= s && cur < e) : (cur >= s || cur < e);
}

export function isDarkNow(prefs, date = new Date()) {
  const p = mergeDisplayPrefs(prefs);
  if (p.theme.mode === "dark") return true;
  if (p.theme.mode === "light") return false;
  return isTimeInWindow(date, p.theme.darkStart, p.theme.darkEnd);
}

/** Pasang/lepas class `theme-dark` di <html>. Return true bila dark aktif. */
export function applyTheme(prefs, date = new Date()) {
  const dark = isDarkNow(prefs, date);
  document.documentElement.classList.toggle("theme-dark", dark);
  return dark;
}
